import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Collapse,
  TablePagination,
  TextField,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Inventory,
  Timeline,
  Schedule,
  DeviceHub,
  ExpandMore,
  ExpandLess,
  FilterList,
  Sort,
  Calculate,
  Warning,
  Search,
  AccountTree,
  TableChart,
} from '@mui/icons-material';
import { goodsAPI, materialStatisticsAPI, productionRoutesAPI } from '../services/api';
import ProductionRouteGraph from '../components/ProductionRouteGraph';

const MaterialStatisticsPage = () => {
  const [goods, setGoods] = useState([]);
  const [selectedGoodId, setSelectedGoodId] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [selectedFormulaIndex, setSelectedFormulaIndex] = useState(0);
  const [statistics, setStatistics] = useState(null);
  const [routeData, setRouteData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [viewMode, setViewMode] = useState('route'); // 'table' 或 'route'
  const [goodDialogOpen, setGoodDialogOpen] = useState(false);

  useEffect(() => {
    fetchGoods();
  }, []);

  const fetchGoods = async () => {
    try {
      setLoading(true);
      const response = await goodsAPI.getAll();
      setGoods(response.data);
    } catch (err) {
      setError('获取物品列表失败');
      console.error('Error fetching goods:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async (goodId, qty, resetFormulaIndex = true) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await materialStatisticsAPI.getStatisticsById(goodId, qty);
      
      setStatistics(response.data);
      if (resetFormulaIndex) {
        setSelectedFormulaIndex(0); // 仅在需要时重置为第一个配方
      }
      setPage(0);
      
    } catch (err) {
      setError(err.response?.data?.error || '获取统计信息失败');
      console.error('Error fetching statistics:', err);
      setStatistics(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchProductionRoutes = async (goodId, qty) => {
    try {
      setLoading(true);
      setError(null);
      
      // 使用新版API
      const response = await productionRoutesAPI.getProductionRoutesV2(goodId, qty);
      
      // 转换节点结构：将新API的扁平结构转换为组件期望的details结构
      const convertNode = (node) => {
        const converted = {
          good_id: node.good_id,
          details: {
            good_id: node.good_id,
            count: node.quantity,
            equipment_id: node.equipment_id,
            time: node.time,
            good_name: node.good_name,
            good_image_name: node.good_image_name,
            equipment_name: node.equipment_name,
            equipment_image_name: node.equipment_image_name,
            mat_a_id: -1,  // 新API不返回这些字段，组件也不需要
            mat_b_id: -1
          },
          children: (node.children || []).map(child => convertNode(child))
        };
        return converted;
      };
      
      // 转换新API返回的数据结构以适配现有组件
      const adaptedData = {
        ...response.data,
        routes: response.data.routes.map(route => ({
          root_nodes: [convertNode(route)]
        }))
      };
      
      setRouteData(adaptedData);
      
      // 打印缓存统计信息
      if (response.data.cache_stats) {
        console.log('缓存统计:', response.data.cache_stats);
      }
      
    } catch (err) {
      setError(err.response?.data?.error || '获取合成路线失败');
      console.error('Error fetching production routes:', err);
      setRouteData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleQuantityChange = (event) => {
    const value = event.target.value;
    // 允许空值，方便用户删除输入
    if (value === '' || value === null) {
      setQuantity('');
      return;
    }
    const numValue = parseInt(value);
    if (!isNaN(numValue) && numValue > 0) {
      setQuantity(numValue);
    }
  };

  const handleSearch = () => {
    if (selectedGoodId) {
      if (viewMode === 'table') {
        fetchStatistics(selectedGoodId, quantity);
      } else {
        fetchProductionRoutes(selectedGoodId, quantity);
      }
    }
  };

  const handleViewModeChange = (event, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);
      setError(null);
      
      // 如果已选择物品，自动获取数据
      if (selectedGoodId) {
        if (newMode === 'table') {
          fetchStatistics(selectedGoodId, quantity, false); // 不重置配方索引
        } else {
          fetchProductionRoutes(selectedGoodId, quantity);
        }
      }
    }
  };

  const handleFormulaChange = (event) => {
    setSelectedFormulaIndex(event.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // 格式化时间显示
  const formatTime = (seconds) => {
    if (!seconds) return '0秒';
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    const parts = [];
    if (hours > 0) parts.push(`${hours}小时`);
    if (minutes > 0) parts.push(`${minutes}分钟`);
    if (secs > 0 || parts.length === 0) parts.push(`${secs}秒`);
    
    return parts.join(' ');
  };

  // 获取当前选中的配方
  const currentFormula = statistics?.formulas?.[selectedFormulaIndex];
  const materials = currentFormula?.all_materials || [];
  
  // 分页后的材料
  const paginatedMaterials = materials.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Container maxWidth="lg" sx={{ 
      py: 4,
      // 只防止横向的过度滚动触发浏览器手势，允许纵向滚动链
      overscrollBehaviorX: 'contain',
      overscrollBehaviorY: 'auto'
    }}>
      {/* 标题 */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          <Calculate sx={{ verticalAlign: 'middle', mr: 2, fontSize: 40 }} />
          原料统计系统
        </Typography>
        <Typography variant="body1" color="text.secondary">
          分析物品的生产链，展示所有所需的原料、生产时间和设备
        </Typography>
      </Box>

      {/* 选择物品卡片 */}
      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={4}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => setGoodDialogOpen(true)}
              disabled={loading}
              sx={{ 
                height: 56,
                justifyContent: 'space-between',
                textTransform: 'none',
                fontSize: '1rem'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {selectedGoodId && goods.find(g => g.id === selectedGoodId)?.image_name && (
                  <Box
                    component="img"
                    src={`http://139.224.28.156:5000/images/${goods.find(g => g.id === selectedGoodId).image_name}`}
                    alt={goods.find(g => g.id === selectedGoodId)?.name}
                    sx={{ width: 30, height: 30, objectFit: 'contain' }}
                  />
                )}
                <Typography>
                  {selectedGoodId ? goods.find(g => g.id === selectedGoodId)?.name : '选择要分析的物品'}
                </Typography>
              </Box>
              <Inventory />
            </Button>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              type="number"
              label="生产数量"
              value={quantity}
              onChange={handleQuantityChange}
              disabled={loading}
              inputProps={{ min: 1 }}
              helperText="输入需要生产的数量"
              sx={{
                '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                  WebkitAppearance: 'none',
                  margin: 0,
                },
                '& input[type=number]': {
                  MozAppearance: 'textfield',
                },
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={handleSearch}
              disabled={!selectedGoodId || loading || !quantity || quantity <= 0}
              startIcon={loading ? <CircularProgress size={20} /> : <Search />}
              sx={{ height: 56 }}
            >
              {loading ? '查询中...' : '查询'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* 物品选择对话框 */}
      <Dialog 
        open={goodDialogOpen} 
        onClose={() => setGoodDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            height: { xs: '70vh', sm: '60vh', md: '55vh' },
            maxHeight: { xs: '70vh', sm: '60vh', md: '55vh' },
            m: { xs: 1, sm: 2 }
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          选择要分析的物品
        </DialogTitle>
        <DialogContent sx={{ overflow: 'auto', pt: 2 }}>
          <Box sx={{ 
            display: 'grid', 
            // 响应式网格：手机4列，平板8列，桌面15列
            gridTemplateColumns: { 
              xs: 'repeat(5, 1fr)',
              sm: 'repeat(8, 1fr)',
              md: 'repeat(11, 1fr)',
              lg: 'repeat(15, 1fr)'
            }, 
            gap: { xs: 1, sm: 1.5 }, 
            py: 1
          }}>
            {goods.map(good => (
              <Box
                key={good.id}
                onClick={() => {
                  setSelectedGoodId(good.id);
                  setGoodDialogOpen(false);
                  // 自动触发查询
                  if (viewMode === 'table') {
                    fetchStatistics(good.id, quantity);
                  } else {
                    fetchProductionRoutes(good.id, quantity);
                  }
                }}
                sx={{
                  position: 'relative',
                  aspectRatio: '1',
                  cursor: 'pointer',
                  border: selectedGoodId === good.id ? '3px solid #1976d2' : '2px solid #ddd',
                  borderRadius: 1,
                  overflow: 'visible',
                  transition: 'all 0.2s',
                  bgcolor: 'white',
                  // 移动端优化
                  minWidth: { xs: '60px', sm: '70px' },
                  minHeight: { xs: '60px', sm: '70px' },
                  '&:hover': {
                    transform: { xs: 'scale(1.05)', sm: 'scale(1.15)' },
                    zIndex: 10,
                    boxShadow: 3,
                    '& .good-name-tooltip': {
                      opacity: 1,
                      visibility: 'visible',
                    }
                  },
                  // 移动端点击效果
                  '&:active': {
                    transform: 'scale(0.95)',
                    '& .good-name-tooltip': {
                      opacity: 1,
                      visibility: 'visible',
                    }
                  }
                }}
              >
                <Box
                  component="img"
                  src={`http://139.224.28.156:5000/images/${good.image_name}`}
                  alt={good.name}
                  sx={{ 
                    width: '100%', 
                    height: '100%', 
                    objectFit: 'contain',
                    p: 0.5
                  }}
                  onError={(e) => { 
                    e.target.style.display = 'none';
                    const parent = e.target.parentElement;
                    if (parent) {
                      parent.style.display = 'flex';
                      parent.style.alignItems = 'center';
                      parent.style.justifyContent = 'center';
                      parent.style.fontSize = '12px';
                      parent.innerHTML = good.name.substring(0, 2);
                    }
                  }}
                />
                <Box
                  className="good-name-tooltip"
                  sx={{
                    position: 'absolute',
                    top: { xs: '-30px', sm: '25%' },
                    left: '50%',
                    transform: { xs: 'translateX(-50%)', sm: 'translate(-50%, -50%)' },
                    bgcolor: 'rgba(0, 0, 0, 0.85)',
                    color: 'white',
                    px: { xs: 1, sm: 1.5 },
                    py: { xs: 0.3, sm: 0.5 },
                    borderRadius: 1,
                    fontSize: { xs: '11px', sm: '13px' },
                    whiteSpace: 'nowrap',
                    opacity: 0,
                    visibility: 'hidden',
                    transition: 'opacity 0.2s',
                    zIndex: 20,
                    pointerEvents: 'none',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                  }}
                >
                  {good.name}
                </Box>
              </Box>
            ))}
          </Box>
        </DialogContent>
      </Dialog>

      {/* 错误提示 */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* 视图模式切换 */}
      {selectedGoodId && (
        <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="h6">
              数据展示模式
            </Typography>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewModeChange}
              aria-label="view mode"
              color="primary"
            >
              <ToggleButton value="route" aria-label="route view">
                <AccountTree sx={{ mr: 1 }} />
                合成路线图
              </ToggleButton>
              <ToggleButton value="table" aria-label="table view">
                <TableChart sx={{ mr: 1 }} />
                表格展示
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Paper>
      )}

      统计结果
      {viewMode === 'table' && statistics && (
        <>
          {/* 统计摘要卡片
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Inventory color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6" color="text.secondary">
                      目标物品
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                    {statistics.good_image_name && (
                      <Box
                        component="img"
                        src={`http://139.224.28.156:5000/images/${statistics.good_image_name}`}
                        alt={statistics.good_name}
                        sx={{ width: 40, height: 40, objectFit: 'contain' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    )}
                    <Box>
                      <Typography variant="h6" fontWeight="bold">
                        {statistics.good_name || `物品 ${statistics.good_id}`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ID: {statistics.good_id}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    生产数量: {statistics.quantity}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <FilterList color="secondary" sx={{ mr: 1 }} />
                    <Typography variant="h6" color="text.secondary">
                      配方数量
                    </Typography>
                  </Box>
                  <Typography variant="h4" fontWeight="bold">
                    {statistics.formula_count || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    种不同配方
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Timeline color="success" sx={{ mr: 1 }} />
                    <Typography variant="h6" color="text.secondary">
                      当前配方材料
                    </Typography>
                  </Box>
                  <Typography variant="h4" fontWeight="bold">
                    {materials.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    种材料
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <DeviceHub color="warning" sx={{ mr: 1 }} />
                    <Typography variant="h6" color="text.secondary">
                      最大深度
                    </Typography>
                  </Box>
                  <Typography variant="h4" fontWeight="bold">
                    {currentFormula?.max_depth || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    生产层级
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid> */}

          {/* 配方选择器 */}
          {statistics.formulas && statistics.formulas.length > 0 && (
            <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={8}>
                  <FormControl fullWidth>
                    <InputLabel>选择配方查看详情</InputLabel>
                    <Select
                      value={selectedFormulaIndex}
                      onChange={handleFormulaChange}
                      label="选择配方查看详情"
                    >
                      {statistics.formulas.map((formula, index) => (
                        <MenuItem key={index} value={index}>
                          配方 {index + 1}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Chip
                    label={`当前: 配方 ${selectedFormulaIndex + 1}`}
                    color="primary"
                    size="large"
                    icon={<FilterList />}
                  />
                </Grid>
              </Grid>
            </Paper>
          )}

          {/* 材料表格 */}
          <Paper elevation={2} sx={{ mb: 4 }}>
            <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="h6" gutterBottom>
                配方 {selectedFormulaIndex + 1} - 材料详情
              </Typography>
              <Typography variant="body2" color="text.secondary">
                显示该配方中所有需要的材料、数量、生产时间和所需设备
              </Typography>
            </Box>

            {materials.length > 0 ? (
              <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell align="center" width="80px">图片</TableCell>
                        <TableCell align="left">材料名称</TableCell>
                        <TableCell align="center">数量/分</TableCell>
                        <TableCell align="center">传送带</TableCell>
                        <TableCell align="left">设备</TableCell>
                        {/* <TableCell align="center">生产深度</TableCell> */}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedMaterials.map((material, index) => (
                        <TableRow 
                          key={index}
                          hover
                          sx={{
                            bgcolor: material.material_id === statistics.good_id ? 'primary.50' : 'inherit',
                            '&:hover': {
                              bgcolor: material.material_id === statistics.good_id ? 'primary.100' : 'action.hover',
                            }
                          }}
                        >
                          <TableCell align="center">
                            {material.material_image_name ? (
                              <Box
                                component="img"
                                src={`http://139.224.28.156:5000/images/${material.material_image_name}`}
                                alt={material.material_name}
                                sx={{ 
                                  width: 50, 
                                  height: 50, 
                                  objectFit: 'contain',
                                  borderRadius: 1,
                                  border: material.material_id === statistics.good_id ? '2px solid' : '1px solid',
                                  borderColor: material.material_id === statistics.good_id ? 'primary.main' : 'divider',
                                  p: 0.5
                                }}
                                onError={(e) => { 
                                  e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="50" height="50"%3E%3Crect width="50" height="50" fill="%23f0f0f0"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999" font-size="12"%3E无图%3C/text%3E%3C/svg%3E';
                                }}
                              />
                            ) : (
                              <Box
                                sx={{
                                  width: 50,
                                  height: 50,
                                  bgcolor: 'grey.200',
                                  borderRadius: 1,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                <Inventory color="disabled" />
                              </Box>
                            )}
                          </TableCell>
                          <TableCell align="left">
                            <Box>
                              <Typography fontWeight="medium" variant="body1">
                                {material.material_name || `材料 ${material.material_id}`}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {material.material_id === statistics.good_id && (
                                  <Chip
                                    label="目标"
                                    size="small"
                                    color="primary"
                                    sx={{height: 18 }}
                                  />
                                )}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            {/* <Chip
                              label={(material.quantity).toFixed(2)}
                              // color="secondary"
                              size="medium"
                            /> */}
                            <Typography>
                                {(material.quantity*1).toFixed(2)}
                              </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                              {/* <Schedule fontSize="small" color="action" /> */}
                              <Typography>
                                {/* {formatTime(material.time)} */}
                                {(material.quantity/30).toFixed(2)}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="left">
                            {material.equipment_id === -1 ? (
                              <Chip
                                label="基础原料"
                                size="small"
                                color="success"
                                variant="outlined"
                              />
                            ) : material.equipment_name ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {material.equipment_image_name && (
                                  <Box
                                    component="img"
                                    src={`http://139.224.28.156:5000/images/${material.equipment_image_name}`}
                                    alt={material.equipment_name}
                                    sx={{ width: 32, height: 32, objectFit: 'contain' }}
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                  />
                                )}
                                <Box>
                                  <Typography variant="body2" fontWeight="medium">
                                    {material.equipment_name}
                                  </Typography>
                                </Box>
                                <Box>
                                  <Typography variant="body2" fontWeight="medium">
                                    {(material.quantity/(60/material.time)).toFixed(2)}
                                  </Typography>
                                </Box>
                              </Box>
                            ) : (
                              <Chip
                                label={`设备 ${material.equipment_id}`}
                                size="small"
                                color="info"
                                icon={<DeviceHub />}
                              />
                            )}
                          </TableCell>
                          {/* <TableCell align="center">
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  bgcolor: `hsl(${material.depth * 30}, 70%, 50%)`
                                }}
                              />
                              <Typography fontWeight="medium">
                                深度 {material.depth}
                              </Typography>
                            </Box>
                          </TableCell> */}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  component="div"
                  count={materials.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelRowsPerPage="每页行数:"
                  labelDisplayedRows={({ from, to, count }) => `${from}-${to} 共 ${count}`}
                />
              </>
            ) : (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Alert severity="info">
                  该配方没有材料数据，可能是基础原料
                </Alert>
              </Box>
            )}
          </Paper>

          {/* 缓存统计信息 */}
          {/* {statistics.cache_stats && (
            <Paper elevation={2} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Timeline />
                缓存统计
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                  <Typography variant="body2" color="text.secondary">缓存命中</Typography>
                  <Typography variant="h6">{statistics.cache_stats.hits || 0}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="body2" color="text.secondary">缓存未命中</Typography>
                  <Typography variant="h6">{statistics.cache_stats.misses || 0}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="body2" color="text.secondary">缓存大小</Typography>
                  <Typography variant="h6">{statistics.cache_stats.size || 0}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="body2" color="text.secondary">命中率</Typography>
                  <Typography variant="h6">
                    {statistics.cache_stats.hits && statistics.cache_stats.misses
                      ? `${((statistics.cache_stats.hits / (statistics.cache_stats.hits + statistics.cache_stats.misses)) * 100).toFixed(1)}%`
                      : 'N/A'}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          )} */}
        </>
      )}

      {/* 合成路线图展示 */}
      {viewMode === 'route' && routeData && (
        <>
          {/* 配方选择器 */}
          {routeData.routes && routeData.routes.length > 0 && (
            <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={8}>
                  <FormControl fullWidth>
                    <InputLabel>选择路线查看详情</InputLabel>
                    <Select
                      value={selectedFormulaIndex}
                      onChange={handleFormulaChange}
                      label="选择路线查看详情"
                    >
                      {routeData.routes.map((route, index) => (
                        <MenuItem key={index} value={index}>
                          路线 {index + 1}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Chip
                    label={`当前: 路线 ${selectedFormulaIndex + 1}`}
                    color="primary"
                    size="large"
                    icon={<AccountTree />}
                  />
                </Grid>
              </Grid>
            </Paper>
          )}
          
          <ProductionRouteGraph 
            key={`route-${selectedGoodId}-${quantity}-${selectedFormulaIndex}`}
            routeData={routeData} 
            selectedRouteIndex={selectedFormulaIndex} 
          />
        </>
      )}

      {/* 初始状态提示 */}
      {!statistics && !routeData && !error && !loading && (
        <Paper elevation={2} sx={{ p: 4, textAlign: 'center' }}>
          <Calculate sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            请选择要分析的物品
          </Typography>
          <Typography variant="body2" color="text.disabled">
            选择一个物品后，系统将分析其所有生产方案和所需原料
          </Typography>
        </Paper>
      )}
    </Container>
  );
};

export default MaterialStatisticsPage;